import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import AutomationService, {
  Automation,
  AutomationRun,
} from "@/app/_services/AutomationService";
import type { TEditorConfiguration } from "@/app/admin/_common/email-builder/documents/editor/core";
import { toast } from "@/app/_utils/toast";

export type AutomationStats = {
  window_days: number;
  total: number;
  completed: number;
  skipped: number;
  failed: number;
  pending: number;
};

export function useAutomationDetailModel() {
  const { automationId } = useParams<{ automationId: string }>();
  const navigate = useNavigate();

  const [automation, setAutomation] = useState<Automation | null>(null);
  const [runs, setRuns] = useState<AutomationRun[]>([]);
  const [stats, setStats] = useState<AutomationStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSavingStatus, setIsSavingStatus] = useState(false);

  const load = useCallback(async () => {
    if (!automationId) return;
    setIsLoading(true);
    try {
      const [detail, runList, statistics] = await Promise.all([
        AutomationService.getAutomationAsync(automationId),
        AutomationService.getRunsAsync(automationId),
        AutomationService.getStatsAsync(automationId),
      ]);
      setAutomation(detail);
      setRuns(runList);
      setStats(statistics);
    } catch (error) {
      console.error(error);
      toast.error("Could not load this automation");
    } finally {
      setIsLoading(false);
    }
  }, [automationId]);

  useEffect(() => {
    load();
  }, [load]);

  /**
   * The cutover control.
   *
   * Flipping mode to "live" is what silences the legacy send for this concern; flipping
   * it back restores it. Both take effect on the next event with no deploy, which is
   * the entire reason the rollout is safe — so this needs to be unambiguous about what
   * just happened rather than a generic "Saved".
   */
  async function changeStatus(body: { status?: string; mode?: string }) {
    if (!automationId) return;
    setIsSavingStatus(true);
    try {
      const updated = await AutomationService.setStatusAsync(automationId, body);
      setAutomation(updated);
      if (body.mode === "live") {
        toast.success(`${updated.name} is live — it will now send for real`);
      } else if (body.mode === "shadow") {
        toast.success(`${updated.name} is back in shadow — nothing will be sent`);
      } else if (body.status) {
        toast.success(`${updated.name} is now ${body.status}`);
      }
    } catch (error: any) {
      console.error(error);
      toast.error(error?.detail ?? "Could not update this automation");
    } finally {
      setIsSavingStatus(false);
    }
  }

  async function saveAutomation(body: Partial<Automation>) {
    if (!automationId) return false;
    try {
      const updated = await AutomationService.updateAutomationAsync(automationId, body);
      setAutomation(updated);
      toast.success("Automation saved");
      return true;
    } catch (error: any) {
      console.error(error);
      // DRF returns field-keyed errors; surface the first rather than a generic message,
      // since these are almost always "this action cannot be used with that event".
      const first =
        error && typeof error === "object"
          ? Object.values(error).flat()[0]
          : null;
      toast.error(typeof first === "string" ? first : "Could not save this automation");
      return false;
    }
  }

  async function saveTemplate(body: {
    key: string;
    subject?: string;
    body?: string;
    body_json?: TEditorConfiguration | null;
    channel?: string;
  }) {
    if (!automationId) return false;
    try {
      await AutomationService.saveTemplateAsync(automationId, body);
      await load();
      toast.success("Template saved");
      return true;
    } catch (error) {
      console.error(error);
      toast.error("Could not save this template");
      return false;
    }
  }

  return {
    automation,
    runs,
    stats,
    isLoading,
    isSavingStatus,
    refetch: load,
    changeStatus,
    saveAutomation,
    saveTemplate,
    goBack: () => navigate("/admin/tools/automations"),
  };
}
